import { useCallback, useEffect, useRef, useState } from 'react';

import { useDismissOnOutside } from './use-dismiss-on-outside';

// A stable ref so the hook's dependency array does not change identity
// every render while the panel is closed.
const NOT_OPEN = { current: null };
import { useAlertNotifications } from '../state/useAlertNotifications';
import { useAlertWords } from '../state/alert-notifications/useAlertWords';
import { useUserPreferences } from '../state/useUserPreferences';
import { alertPlaceFocus } from '../state/show-where';
import { IconBell, IconMail, IconMailOpen } from '../design-system/icons';
import './AlertBell.css';

function formatTime(ts: string): string {
  if (!ts) return '—';
  if (/^\d+$/.test(ts)) return new Date(Number(ts)).toLocaleTimeString();
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleTimeString();
}

/**
 * Read-state envelope icon. Open = unread, closed = read.
 * Clicking toggles the read state of the alert (Gmail-style).
 */
function EnvelopeIcon({ open }: { open: boolean }) {
  return (
    <span className="nx-alert-bell__envelope" data-testid="envelope-icon">
      {open ? <IconMailOpen size={15} /> : <IconMail size={15} />}
    </span>
  );
}

/**
 * The bell dropdown: recent alerts with per-row read/unread state.
 *
 * No archive view any more — the old archive lived only in this
 * browser's localStorage, so "archiving" hid a row here while it
 * stayed open on the Alerts tab, in the badge, and for every other
 * operator. Alerts leave the bell the same way they leave everything
 * else now: resolved (or the device muted) on the Alerts screen,
 * which closes the server row for everyone.
 */
export function AlertBell() {
  const {
    recent,
    unreadCount,
    markRead,
    markUnread,
    markAllRead,
    handleAlertAction,
    showAlertWhere,
  } = useAlertNotifications();
  const { prefs } = useUserPreferences();
  // The same words the toasts use: hotel words for a site audience, the
  // engine's own text for ops (plan W5 step 12).
  const wordsFor = useAlertWords();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [shaking, setShaking] = useState(false);
  const prevUnreadRef = useRef(0);

  // THE GUARD USED TO BE `if (isOpen) return`, which is backwards: the
  // listeners existed only while the panel was CLOSED, so it dismissed
  // on neither an outside press nor Escape, on any device. On a phone,
  // where the panel spans the whole bar, re-tapping the trigger was the
  // only way to close it. Now the shared hook, which takes the trigger
  // as `ignore` so pressing it still toggles rather than closing and
  // reopening in one gesture.
  useDismissOnOutside(
    isOpen ? panelRef : NOT_OPEN,
    useCallback(() => setIsOpen(false), []),
    buttonRef,
  );

  // Trigger shake animation when unread count transitions 0 → >0.
  useEffect(() => {
    if (prevUnreadRef.current === 0 && unreadCount > 0) {
      setShaking(true);
      const handle = window.setTimeout(() => setShaking(false), 700);
      prevUnreadRef.current = unreadCount;
      return () => window.clearTimeout(handle);
    }
    prevUnreadRef.current = unreadCount;
    return undefined;
  }, [unreadCount]);

  return (
    <div className="nx-alert-bell">
      <button
        ref={buttonRef}
        type="button"
        className="nx-alert-bell__btn"
        onClick={() => setIsOpen((v) => !v)}
        aria-label={`Alerts (${unreadCount} unread)`}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        data-testid="alert-bell"
      >
        <span
          className={`nx-alert-bell__icon${shaking ? ' nx-alert-bell__icon--shake' : ''}`}
          data-testid="alert-bell-icon"
        >
          <IconBell size={19} />
        </span>
        {unreadCount > 0 && (
          <span className="nx-alert-bell__badge" data-testid="alert-bell-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {isOpen && (
        <div
          ref={panelRef}
          className="nx-alert-bell__panel"
          role="dialog"
          aria-label="Recent alerts"
          data-testid="alert-bell-panel"
        >
          <header className="nx-alert-bell__head">
            <span>Recent alerts</span>
            <div className="nx-alert-bell__head-actions">
              <button
                type="button"
                onClick={() => {
                  if (unreadCount > 0) markAllRead();
                }}
                disabled={unreadCount === 0}
                aria-label="Mark all read"
                data-testid="alert-bell-mark-all-read"
              >
                Mark all read
              </button>
            </div>
          </header>
          {recent.length === 0 ? (
            <div className="nx-alert-bell__empty">No actionable alerts yet.</div>
          ) : (
            <ul
              className="nx-alert-bell__list"
              style={{ maxHeight: `${prefs.ui.bellMaxHeightPx}px` }}
              data-testid="alert-bell-list"
            >
              {recent.map((a) => {
                const isRead = a.isRead;
                const words = wordsFor(a);
                return (
                  <li
                    key={a.id}
                    className={`nx-alert-bell__item nx-alert-bell__item--${a.severity}${isRead ? ' nx-alert-bell__item--read' : ''}`}
                    data-testid={`alert-bell-item-${a.id}`}
                  >
                    <div className="nx-alert-bell__item-main">
                      <button
                        type="button"
                        className="nx-alert-bell__envelope-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isRead) markUnread(a.id);
                          else markRead(a.id);
                        }}
                        title={isRead ? 'Mark as unread' : 'Mark as read'}
                        aria-label={isRead ? 'Mark as unread' : 'Mark as read'}
                        aria-pressed={isRead}
                        data-testid={`alert-bell-envelope-${a.id}`}
                      >
                        <EnvelopeIcon open={!isRead} />
                      </button>
                      <button
                        type="button"
                        className="nx-alert-bell__item-btn"
                        onClick={() => {
                          handleAlertAction(a);
                          setIsOpen(false);
                        }}
                      >
                        <div className="nx-alert-bell__item-head">
                          <span className="nx-alert-bell__sev">{a.severity}</span>
                          <span className="nx-alert-bell__time">
                            {/* A site audience reads every row's time on the
                                site's clock, labelled "(device time)" when
                                the site declares no zone; hotel words show
                                their own "Since", never the device's clock,
                                unlabelled, beside it. Ops keep the row's
                                own time. */}
                            {words.headTime ?? formatTime(a.timestamp)}
                          </span>
                        </div>
                        <div className="nx-alert-bell__src">{words.source}</div>
                        {!words.translated && a.subtitle && (
                          <div className="nx-alert-bell__sub">{a.subtitle}</div>
                        )}
                        <div className="nx-alert-bell__msg">{words.message}</div>
                      </button>
                    </div>
                    {/* The toast's "Show where", for a row about one place
                        (robot, lift, door, task). It points; it never
                        opens a drawer. */}
                    {alertPlaceFocus(a) !== null && (
                      <button
                        type="button"
                        className="nx-alert-bell__show-where"
                        onClick={() => {
                          showAlertWhere(a);
                          setIsOpen(false);
                        }}
                        data-testid={`alert-bell-show-where-${a.id}`}
                      >
                        Show where
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
